/**
 * SetFiltersPanel - Filter controls for the Set Details page
 *
 * Provides rarity, color, type, CMC, ownership, and deck membership filters.
 * Uses collapsible filter groups with ephemeral state (resets on navigation).
 */
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { IoClose } from 'react-icons/io5';

import CardTypeFilter from '@/components/CardTypeFilter/CardTypeFilter';
import CmcFilter from '@/components/CmcFilter/CmcFilter';
import CollapsibleFilterGroup from '@/components/CollapsibleFilterGroup/CollapsibleFilterGroup';
import ColorFilter from '@/components/ColorFilter/ColorFilter';
import FilterChip from '@/components/FilterChip/FilterChip';
import FilterDivider from '@/components/FilterDivider/FilterDivider';
import InDeckFilter, { type InDeckFilterValue } from '@/components/InDeckFilter/InDeckFilter';
import InListFilter, { type InListFilterValue } from '@/components/InListFilter/InListFilter';
import RarityBadge from '@/components/RarityBadge/RarityBadge';
import { decksStateAtom } from '@/features/decks/state/decksAtom';
import { listsStateAtom } from '@/features/lists/state/listsAtom';
import type { CardType } from '@/types/schemas';

import useSetFilters, { FILTER_RARITIES, OWNERSHIP_OPTIONS } from '../hooks/useSetFilters';

// Default collapsed state: Colors open, all others closed
const DEFAULT_COLLAPSED_STATE: Record<string, boolean> = {
  colors: false, // Open
  rarity: true, // Closed
  types: true, // Closed
  cmc: true, // Closed
  ownership: true, // Closed
};

export default function SetFiltersPanel(): ReactElement {
  const {
    filters,
    toggleRarity,
    toggleColor,
    toggleSpecialColor,
    toggleCardType,
    toggleCmc,
    setOwnership,
    setInDeckFilter,
    setDeckIdFilter,
    setInListFilter,
    setListIdFilter,
    clearFilters,
    hasActiveFilters,
  } = useSetFilters();

  // Collapse state - ephemeral (resets on navigation)
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(DEFAULT_COLLAPSED_STATE);

  // Decks for InDeckFilter
  const decksState = useAtomValue(decksStateAtom);
  const decks = decksState.data ?? [];

  // Lists for InListFilter
  const listsState = useAtomValue(listsStateAtom);
  const lists = listsState.data ?? [];

  // Reset collapsed state to defaults
  const resetCollapsedState = useCallback(() => {
    setCollapsedGroups(DEFAULT_COLLAPSED_STATE);
  }, []);

  const toggleCollapsed = (group: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  // Calculate isLocked per group (locked when has active filters)
  const isRarityLocked = filters.rarities.length > 0;
  const isColorsLocked = filters.colors.length > 0 || filters.specialColors.length > 0;
  const isTypesLocked = filters.cardTypes.length > 0;
  const isCmcLocked = filters.cmcValues.length > 0;
  const isOwnershipLocked = filters.ownership !== 'all';

  // Collapsed state (locked groups ignore this)
  const isRarityCollapsed = collapsedGroups.rarity ?? DEFAULT_COLLAPSED_STATE.rarity;
  const isColorsCollapsed = collapsedGroups.colors ?? DEFAULT_COLLAPSED_STATE.colors;
  const isTypesCollapsed = collapsedGroups.types ?? DEFAULT_COLLAPSED_STATE.types;
  const isCmcCollapsed = collapsedGroups.cmc ?? DEFAULT_COLLAPSED_STATE.cmc;
  const isOwnershipCollapsed = collapsedGroups.ownership ?? DEFAULT_COLLAPSED_STATE.ownership;

  // Handle clear: reset filters and collapsed state
  const handleClear = useCallback(() => {
    clearFilters();
    resetCollapsedState();
  }, [clearFilters, resetCollapsedState]);

  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      {/* Color filters - collapsible, first by default */}
      <CollapsibleFilterGroup
        label="Colors"
        isCollapsed={isColorsCollapsed}
        activeCount={filters.colors.length + filters.specialColors.length}
        onToggle={() => toggleCollapsed('colors')}
        isLocked={isColorsLocked}
      >
        <ColorFilter
          colors={filters.colors}
          specialColors={filters.specialColors}
          onColorToggle={toggleColor}
          onSpecialColorToggle={toggleSpecialColor}
        />
      </CollapsibleFilterGroup>

      <FilterDivider />

      {/* Rarity filters - collapsible */}
      <CollapsibleFilterGroup
        label="Rarity"
        isCollapsed={isRarityCollapsed}
        activeCount={filters.rarities.length}
        onToggle={() => toggleCollapsed('rarity')}
        isLocked={isRarityLocked}
      >
        {FILTER_RARITIES.map((rarity) => (
          <RarityBadge
            key={rarity}
            rarity={rarity}
            variant="chip-filter"
            isActive={filters.rarities.includes(rarity)}
            onClick={() => toggleRarity(rarity)}
          />
        ))}
      </CollapsibleFilterGroup>

      <FilterDivider />

      {/* Type filters - collapsible */}
      <CollapsibleFilterGroup
        label="Types"
        isCollapsed={isTypesCollapsed}
        activeCount={filters.cardTypes.length}
        onToggle={() => toggleCollapsed('types')}
        isLocked={isTypesLocked}
      >
        <CardTypeFilter
          selectedTypes={filters.cardTypes as CardType[]}
          onToggle={(type) => toggleCardType(type as (typeof filters.cardTypes)[number])}
        />
      </CollapsibleFilterGroup>

      <FilterDivider />

      {/* CMC filters - collapsible */}
      <CollapsibleFilterGroup
        label="CMC"
        isCollapsed={isCmcCollapsed}
        activeCount={filters.cmcValues.length}
        onToggle={() => toggleCollapsed('cmc')}
        isLocked={isCmcLocked}
      >
        <CmcFilter values={filters.cmcValues} onToggle={toggleCmc} />
      </CollapsibleFilterGroup>

      <FilterDivider />

      {/* Ownership filters - collapsible */}
      <CollapsibleFilterGroup
        label="Owned"
        isCollapsed={isOwnershipCollapsed}
        activeCount={filters.ownership !== 'all' ? 1 : 0}
        onToggle={() => toggleCollapsed('ownership')}
        isLocked={isOwnershipLocked}
      >
        {OWNERSHIP_OPTIONS.map((option) => (
          <FilterChip
            key={option.value}
            label={option.label}
            isActive={filters.ownership === option.value}
            onClick={() => setOwnership(option.value)}
          />
        ))}
      </CollapsibleFilterGroup>

      {/* In Deck filter */}
      {decks.length > 0 && (
        <>
          <FilterDivider />
          <InDeckFilter
            value={filters.inDeckFilter as InDeckFilterValue | undefined}
            deckId={filters.deckIdFilter}
            onValueChange={setInDeckFilter}
            onDeckChange={setDeckIdFilter}
            decks={decks}
          />
        </>
      )}

      {/* In List filter */}
      {lists.length > 0 && (
        <>
          <FilterDivider />
          <InListFilter
            value={filters.inListFilter as InListFilterValue | undefined}
            listId={filters.listIdFilter}
            onValueChange={setInListFilter}
            onListChange={setListIdFilter}
            lists={lists}
          />
        </>
      )}

      {/* Clear filters - pushed to the right */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleClear}
          className="text-danger hover:bg-danger/10 focus-visible:ring-danger focus-visible:ring-offset-void ml-auto flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          aria-label="Clear all filters"
        >
          <IoClose className="h-4 w-4" aria-hidden="true" />
          Clear
        </button>
      )}
    </div>
  );
}
